/**
 * Module dependencies.
 */

import { KeyboardEvent, MouseEvent } from 'react';
import { RouterLink } from 'src/components/core/links/router-link';
import { isExternalUrl } from 'src/core/utils/url';
import { regexes } from './regexes';

/**
 * `OnClickType` type.
 */

type OnClickType =
  | undefined
  | ((event?: MouseEvent<HTMLAnchorElement>) => void);

/**
 * Export `handleKeyboardEventProps` util.
 */

export function handleKeyboardEventProps(onClick: OnClickType) {
  if (!onClick) {
    return {};
  }

  return {
    onKeyDown: ({ code }: KeyboardEvent<HTMLAnchorElement>) => {
      if (['Enter', 'Space'].includes(code)) {
        onClick();
      }
    }
  };
}

/**
 * Export `isExternalLink`.
 */

export function isExternalLink(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return (
    regexes.url.test(url) ||
    regexes.emailLink.test(url) ||
    regexes.telLink.test(url)
  );
}

/**
 * `getClickProps` util.
 */

const getClickProps = (onClick?: OnClickType) =>
  !onClick
    ? {}
    : {
      ...handleKeyboardEventProps(onClick),
      role: 'button',
      tabIndex: 0
    };

/**
 * `getLinkProps` util.
 */

const getLinkProps = ({ href, target }: any) =>
  !isExternalUrl(href)
    ? {}
    : {
      rel: 'noopener',
      target: target ?? '_blank'
    };

/**
 * Export `setLinkProps` util.
 */

export function setLinkProps(props: any) {
  const { as, href, onClick } = props;
  const isExternal = href && isExternalUrl(href);
  const element =
    as ||
    (href && !isExternal && RouterLink) ||
    (href && isExternal && 'a') ||
    'button';

  return {
    ...getClickProps(onClick),
    ...getLinkProps(props),
    as: element
  };
}

/**
 * Export `setButtonLinkProps` util.
 */

export function setButtonLinkProps(props: any) {
  const linkProps = setLinkProps(props);

  return {
    ...linkProps,
    type: props.type || (linkProps.as === 'button' ? 'button' : null)
  };
}
